import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldCheck, ShieldX, Loader2, Inbox, ChevronRight, Camera, MessageSquare, Building2,
} from "lucide-react";
import { toast } from "sonner";

/** Bandeja del validador: lista todas las validaciones pendientes (sólo admin).
 *  Click en una → dialog con galería + comentario + botones aprobar/rechazar.
 */
export default function ValidacionesPendientes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/validaciones/pendientes");
      setItems(data.items || []);
    } catch (err) {
      toast.error("Error cargando validaciones");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-8 max-w-6xl" data-testid="validaciones-pendientes-page">
      <PageHeader
        title="Validaciones de montaje"
        subtitle="Solicitudes de OK enviadas por los montadores con fotos del trabajo realizado."
      />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-xl p-12 text-center">
          <Inbox className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <div className="text-zinc-300 font-medium">No hay validaciones pendientes</div>
          <div className="text-xs text-zinc-500 mt-2 max-w-md mx-auto">
            Cuando un montador termine un trabajo con flag "Requiere validación" y suba fotos,
            aparecerá aquí para tu OK.
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="validaciones-list">
          {items.map((v) => (
            <ValidacionRow key={v.id} v={v} onOpen={() => setSel(v)} />
          ))}
        </div>
      )}

      {sel && (
        <ValidarDialog v={sel} onClose={() => setSel(null)}
          onResolved={() => { setSel(null); load(); }} />
      )}
    </div>
  );
}

function ValidacionRow({ v, onOpen }) {
  const pet = v.peticion || {};
  const ln = v.linea || {};
  const iter = v.iteracion > 1 ? `Intento #${v.iteracion}` : null;
  return (
    <div className="group border border-zinc-800 bg-zinc-900/40 rounded-lg p-4 cursor-pointer hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-colors"
      onClick={onOpen} data-testid={`validacion-row-${v.id}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0">
          <div className="w-12 h-12 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-cyan-400" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-semibold text-zinc-100">{pet.codigo || pet.nombre || "—"}</span>
            {pet.marca && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-violet-500/10 text-violet-300 border-violet-500/30">{pet.marca}</span>
            )}
            {iter && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-orange-500/15 text-orange-300 border-orange-500/30">{iter}</span>
            )}
            <span className="text-[10px] px-1.5 py-0.5 rounded border bg-emerald-500/10 text-emerald-300 border-emerald-500/30 inline-flex items-center gap-1">
              <Camera className="w-2.5 h-2.5" /> {(v.fotos || []).length}
            </span>
          </div>
          <div className="text-xs text-zinc-400">{ln.descripcion || ln.tipo || "—"}</div>
          <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-1.5 flex-wrap">
            <span>Por <b className="text-zinc-300">{v.solicitado_por_name || "—"}</b></span>
            <span>{new Date(v.solicitado_at).toLocaleString("es-ES")}</span>
            {v.comentario_montador && (
              <span className="inline-flex items-center gap-1 text-zinc-400 italic">
                <MessageSquare className="w-3 h-3" /> "{v.comentario_montador.slice(0, 60)}{v.comentario_montador.length > 60 ? "…" : ""}"
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-cyan-400" />
      </div>
    </div>
  );
}

function ValidarDialog({ v, onClose, onResolved }) {
  const [decision, setDecision] = useState(null);  // "aprobar" | "rechazar"
  const [comentario, setComentario] = useState("");
  const [saving, setSaving] = useState(false);
  const [fotoIdx, setFotoIdx] = useState(0);

  const fotos = v.fotos || [];
  const fotoActiva = fotos[fotoIdx];

  const resolver = async () => {
    if (decision === "rechazar" && !comentario.trim()) {
      toast.error("Indica el motivo del rechazo");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/validaciones/${v.id}/resolver`, {
        aprobada: decision === "aprobar",
        comentario: comentario || null,
      });
      toast.success(decision === "aprobar" ? "✅ Validación aprobada" : "❌ Validación rechazada");
      onResolved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error resolviendo validación");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="validar-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-cyan-400" />
            Validar montaje
            {v.iteracion > 1 && <span className="text-xs bg-orange-500/15 text-orange-300 px-2 py-0.5 rounded">Intento #{v.iteracion}</span>}
          </DialogTitle>
        </DialogHeader>

        {/* Cabecera info */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase text-zinc-500 tracking-wider">Petición</div>
            <div className="font-mono text-amber-300 font-semibold">{v.peticion?.codigo || "—"}</div>
            <div className="text-xs text-zinc-300">{v.peticion?.nombre}</div>
            {v.peticion?.marca && <div className="text-xs text-violet-300 mt-1">{v.peticion.marca}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase text-zinc-500 tracking-wider">Línea</div>
            <div className="text-xs text-zinc-300">{v.linea?.descripcion || v.linea?.tipo || "—"}</div>
            <div className="text-[10px] text-zinc-500 mt-1">
              Por <b className="text-zinc-300">{v.solicitado_por_name}</b> · {new Date(v.solicitado_at).toLocaleString("es-ES")}
            </div>
          </div>
        </div>

        {/* Galería de fotos */}
        {fotos.length > 0 && (
          <div className="mt-3">
            <div className="bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800">
              <img src={fotoActiva?.url} alt={`Foto ${fotoIdx + 1}`}
                className="w-full max-h-[480px] object-contain bg-black"
                data-testid="validacion-foto-activa" />
              {fotoActiva?.comentario && (
                <div className="bg-zinc-900 border-t border-zinc-800 px-4 py-2 text-xs text-zinc-300 italic">
                  {fotoActiva.comentario}
                </div>
              )}
            </div>
            {fotos.length > 1 && (
              <div className="flex gap-2 mt-2 overflow-x-auto">
                {fotos.map((f, idx) => (
                  <button key={idx} onClick={() => setFotoIdx(idx)}
                    className={`shrink-0 w-16 h-16 rounded border-2 overflow-hidden ${
                      idx === fotoIdx ? "border-cyan-500" : "border-zinc-800 opacity-60 hover:opacity-100"
                    }`}>
                    <img src={f.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Comentario del montador */}
        {v.comentario_montador && (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3 text-sm text-zinc-300 mt-2">
            <div className="text-[10px] uppercase text-zinc-500 tracking-wider mb-1">Comentario del montador</div>
            "{v.comentario_montador}"
          </div>
        )}

        {/* Decisión */}
        {!decision ? (
          <div className="flex gap-2 mt-4">
            <Button onClick={() => setDecision("aprobar")}
              className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
              data-testid="btn-decision-aprobar">
              <ShieldCheck className="w-4 h-4 mr-2" /> Aprobar (dar OK)
            </Button>
            <Button onClick={() => setDecision("rechazar")} variant="outline"
              className="flex-1 bg-transparent border-red-500/40 text-red-300 hover:bg-red-500/10"
              data-testid="btn-decision-rechazar">
              <ShieldX className="w-4 h-4 mr-2" /> Rechazar
            </Button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div className={`rounded-lg p-3 text-sm ${
              decision === "aprobar"
                ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200"
                : "bg-red-500/10 border border-red-500/30 text-red-200"
            }`}>
              {decision === "aprobar"
                ? "✅ Vas a aprobar el montaje. La línea se cerrará automáticamente y el montador recibirá el OK."
                : "❌ Vas a rechazar. Indica el motivo: el montador deberá subir nuevas fotos del trabajo corregido."}
            </div>
            <div>
              <div className="text-xs text-zinc-400 mb-1">
                {decision === "aprobar" ? "Comentario (opcional)" : "Motivo del rechazo *"}
              </div>
              <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)}
                rows={3} required={decision === "rechazar"}
                placeholder={decision === "rechazar" ? "Ej: La barra superior no está alineada, debe repetirse el ajuste lateral." : ""}
                className="bg-zinc-900 border-zinc-800"
                data-testid="validar-comentario" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => { setDecision(null); setComentario(""); }}
                disabled={saving} className="text-zinc-400">Cambiar decisión</Button>
              <Button onClick={resolver} disabled={saving || (decision === "rechazar" && !comentario.trim())}
                className={decision === "aprobar"
                  ? "bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
                  : "bg-red-500 hover:bg-red-400 text-white font-semibold"}
                data-testid="btn-confirmar-decision">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando…</>
                  : decision === "aprobar" ? "✅ Confirmar OK" : "❌ Confirmar rechazo"}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving} className="text-zinc-500">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
